import { PlusOutlined } from '@ant-design/icons';
import { Card } from 'antd';
import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation } from 'react-relay';
import styled from 'styled-components';

import CreateAttachmentMutation from '../../graphql/mutations/CreateAttachmentMutation';
import { ComponentsDispatch } from '../../store';
import { addSpinnerItem, removeSpinnerItem } from '../../store/slices/spinnerSlice';

const FixedWidthContainer = styled.div`
    width: 100px;
    margin: 8px;
    flex-shrink: 0;
`;

const StyledCard = styled(Card)`
    height: 108px;
    .ant-card-body {
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 6px;
    }
`;

interface IAttachmentUploadButton {
    model_id: string;
    model_type: string;
    connectionId: string;
}

const AttachmentUploadButton: React.FC<IAttachmentUploadButton> = ({ model_id, model_type, connectionId }) => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const [commitCreate, inFlightCreate] = useMutation(CreateAttachmentMutation);

    useEffect(() => {
        if (inFlightCreate) {
            dispatch(addSpinnerItem('createAttachment'));
        } else {
            dispatch(removeSpinnerItem('createAttachment'));
        }
    }, [inFlightCreate]);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleAddClick = () => {
        if (!fileInputRef.current) return;
        fileInputRef.current.click();
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                if (typeof reader.result === 'string') {
                    const base64String = reader.result.split(',')[1];
                    resolve(base64String);
                } else {
                    reject(new Error('Failed to convert file to base64'));
                }
            };

            reader.onerror = () => {
                reject(new Error('Error reading file'));
            };

            reader.readAsDataURL(file);
        });
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files?.[0]) return;

        const base64 = await fileToBase64(event.target.files?.[0]);
        const file = {
            name: event.target.files[0].name,
            mime_type: event.target.files[0].type,
            base64,
        };
        if (base64) {
            commitCreate({
                variables: {
                    input: {
                        file,
                        model_id,
                        model_type,
                    },
                    connections: [connectionId],
                },
            });
        }
        event.target.value = '';
    };

    return (
        <FixedWidthContainer>
            <StyledCard hoverable onClick={handleAddClick}>
                <PlusOutlined style={{ fontSize: 24 }} />
            </StyledCard>
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            />
        </FixedWidthContainer>
    );
};

export default AttachmentUploadButton;
